export default function initLazyLoadAnimation(selector = 'img[loading="lazy"]', options = {}) {
  const defaultOptions = {
    threshold: 0,
    onLoad: (img) => {
      img.style.opacity = 1;
    }
  };

  const mergedOptions = { ...defaultOptions, ...options };

  function handleImageLoad(img) {
    mergedOptions.onLoad(img);
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.complete) {
          // Image is already loaded
          handleImageLoad(img);
        } else {
          // Image is still loading
          img.onload = () => handleImageLoad(img);
        }
        observer.unobserve(img);
      }
    });
  }, {
    threshold: mergedOptions.threshold
  });

  // Helper function to set up observation for an image
  function observeImage(img) {
    observer.observe(img);
  }

  // Observe initial images
  const images = document.querySelectorAll(selector);
  images.forEach(img => observeImage(img));

  // Single MutationObserver for everything (new images + src changes)
  const domObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // Handle new images added to DOM
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node itself is a lazy image
          if (node.nodeType === 1 && node.tagName === 'IMG' && node.loading === 'lazy') {
            observeImage(node);
          }
          // Check for lazy images within the added node
          if (node.nodeType === 1 && node.querySelectorAll) {
            const lazyImages = node.querySelectorAll(selector);
            lazyImages.forEach(img => observeImage(img));
          }
        });
      }
      
      // Handle src attribute changes (Alpine reactivity)
      if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
        const img = mutation.target;
        if (img.loading === 'lazy') {
          if (img.complete) {
            handleImageLoad(img);
          } else {
            img.onload = () => handleImageLoad(img);
          }
        }
      }
    });
  });

  // Observe document for both new images AND attribute changes
  domObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src']
  });

  // Return a method to manually observe new images (though DOM observer handles this automatically now)
  return {
    observe: (newImage) => {
      observeImage(newImage);
    }
  };
}
